import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import { alpha } from "@mui/material/styles";
import OptionSwitch from "../../../utils/button/OptionSwitch";
import BaseDialog from "../../../utils/dialog/BaseDialog";

export const CUSTOM_DENSITY_VALUE = "customize";
const SPACING_UNIT_LABEL = "mm";
const SPACING_MIN_VALUE = 0;
const SPACING_MAX_VALUE = 5;
const FONT_UNIT_LABEL = "pt";
const FONT_MIN_VALUE = 6;
const FONT_MAX_VALUE = 14;
const PDF_SETTINGS_RESPONSIVE_ZOOM_THRESHOLD = 1.25;
const PDF_SETTINGS_RESPONSIVE_WIDTH_THRESHOLD = 1100;

const FALLBACK_SPACING_PRESETS = {
  default: {
    sectionSpacerRow: {
      cellPadding: 0,
      fontSize: 1,
      minCellHeight: 0,
    },
    tableStyles: {
      cellPadding: { top: 1.76, bottom: 1.76, left: 1.76, right: 1.76 },
      fontSize: 10,
      minCellHeight: 0,
    },
  },
  compact: {
    sectionSpacerRow: {
      cellPadding: 0,
      fontSize: 1,
      minCellHeight: 2,
    },
    tableStyles: {
      cellPadding: { top: 0.75, bottom: 0.75, left: 1, right: 1 },
      fontSize: 10,
      minCellHeight: 3.8,
    },
  },
};

const SPACING_GROUPS = [
  {
    id: "table",
    title: "Table Styles",
    description: "Controls the main row spacing in the generated table.",
    fields: [
      {
        label: "Font size",
        path: ["tableStyles", "fontSize"],
        unit: FONT_UNIT_LABEL,
        min: FONT_MIN_VALUE,
        max: FONT_MAX_VALUE,
      },
      {
        label: "Row min height",
        path: ["tableStyles", "minCellHeight"],
      },
      {
        label: "Row padding top",
        path: ["tableStyles", "cellPadding", "top"],
      },
      {
        label: "Row padding bottom",
        path: ["tableStyles", "cellPadding", "bottom"],
      },
      {
        label: "Row padding left",
        path: ["tableStyles", "cellPadding", "left"],
      },
      {
        label: "Row padding right",
        path: ["tableStyles", "cellPadding", "right"],
      },
    ],
  },
  {
    id: "section",
    title: "Section",
    description: "Controls the gap between section groups.",
    fields: [
      {
        label: "Section gap height",
        path: ["sectionSpacerRow", "minCellHeight"],
      },
    ],
  },
];

const CUSTOM_SPACING_PRESETS = {
  compact: {
    label: "Compact",
  },
  comfortable: {
    label: "Comfortable",
  },
  spacious: {
    label: "Spacious",
    value: {
      sectionSpacerRow: {
        cellPadding: 0,
        fontSize: 1,
        minCellHeight: 2.5,
      },
      tableStyles: {
        cellPadding: { top: 2.3, bottom: 2.3, left: 2, right: 2 },
        fontSize: 10,
        minCellHeight: 4.5,
      },
    },
  },
};

const CUSTOM_SPACING_PRESET_OPTIONS = Object.entries(
  CUSTOM_SPACING_PRESETS
).map(([value, preset]) => ({
  value,
  label: preset.label,
}));

const cloneSpacingOptions = (value) => {
  if (value == null) return value;
  return JSON.parse(JSON.stringify(value));
};

const getNestedValue = (source, path) =>
  path.reduce((value, key) => value?.[key], source);

const setNestedValue = (source, path, value) => {
  const next = { ...(source || {}) };
  let pointer = next;

  path.forEach((key, index) => {
    if (index === path.length - 1) {
      pointer[key] = value;
      return;
    }

    pointer[key] = { ...(pointer[key] || {}) };
    pointer = pointer[key];
  });

  return next;
};

const sanitizeSpacingOptions = (options) => {
  if (!options || typeof options !== "object") return options;

  const {
    referenceCellPadding: _referenceCellPadding,
    referenceMinCellHeight: _referenceMinCellHeight,
    ...rest
  } = options;

  return rest;
};

const getDensityOptions = (options = []) => {
  const hasCustomize = options.some(
    (option) => option.value === CUSTOM_DENSITY_VALUE
  );

  if (hasCustomize) return options;

  return [
    ...options,
    {
      value: CUSTOM_DENSITY_VALUE,
      label: "Customize",
    },
  ];
};

const getPresetSpacingOptions = (presets, density) =>
  presets?.[density] || FALLBACK_SPACING_PRESETS[density] || {};

const getDisplaySpacingOptions = ({
  selectedDensity,
  spacingOptions,
  spacingPresets,
}) => {
  if (selectedDensity === CUSTOM_DENSITY_VALUE) {
    return spacingOptions || getPresetSpacingOptions(spacingPresets, "default");
  }

  return getPresetSpacingOptions(spacingPresets, selectedDensity);
};

const toPreviewPx = (value, fallback, multiplier = 4) => {
  const parsed = parseFloat(value);
  if (Number.isNaN(parsed)) return fallback;
  return Math.max(0, parsed * multiplier);
};

const getFieldMinValue = (field) => field.min ?? SPACING_MIN_VALUE;
const getFieldMaxValue = (field) => field.max ?? SPACING_MAX_VALUE;
const getFieldUnitLabel = (field) => field.unit ?? SPACING_UNIT_LABEL;

const validateSpacingField = (field, value) => {
  if (value === "" || value == null) return "Enter a number.";

  const minValue = getFieldMinValue(field);
  const maxValue = getFieldMaxValue(field);
  const unitLabel = getFieldUnitLabel(field);
  const parsedValue = parseFloat(value);
  if (Number.isNaN(parsedValue)) return "Enter a number.";
  if (parsedValue < minValue) {
    return `Minimum is ${minValue} ${unitLabel}.`;
  }
  if (parsedValue > maxValue) {
    return `Maximum is ${maxValue} ${unitLabel}.`;
  }

  return "";
};

const hasSpacingErrors = (spacingOptions) =>
  SPACING_GROUPS.some((group) =>
    group.fields.some((field) =>
      Boolean(
        validateSpacingField(field, getNestedValue(spacingOptions, field.path))
      )
    )
  );

const getPdfSettingsZoomLevel = () => {
  if (typeof window === "undefined") return 1;

  if (window.visualViewport?.scale && window.visualViewport.scale !== 1) {
    return window.visualViewport.scale;
  }

  if (window.outerWidth && window.innerWidth) {
    return window.outerWidth / window.innerWidth;
  }

  return window.devicePixelRatio || 1;
};

const shouldUseCompactPdfSettingsLayout = () => {
  if (typeof window === "undefined") return false;

  return (
    getPdfSettingsZoomLevel() >= PDF_SETTINGS_RESPONSIVE_ZOOM_THRESHOLD ||
    window.innerWidth <= PDF_SETTINGS_RESPONSIVE_WIDTH_THRESHOLD
  );
};

const SpacingPreview = ({ spacingOptions }) => {
  const tablePadding = spacingOptions?.tableStyles?.cellPadding || {};
  const fontSize = spacingOptions?.tableStyles?.fontSize;
  const rowMinHeight = spacingOptions?.tableStyles?.minCellHeight;
  const sectionGapHeight = spacingOptions?.sectionSpacerRow?.minCellHeight;

  const baseCellSx = {
    borderBottom: "1px solid",
    borderColor: "divider",
    fontSize: `${toPreviewPx(fontSize, 12, 1.3)}px`,
    minHeight: toPreviewPx(rowMinHeight, 24, 6),
    pb: toPreviewPx(tablePadding.bottom, 5),
    pl: toPreviewPx(tablePadding.left, 8),
    pr: toPreviewPx(tablePadding.right, 8),
    pt: toPreviewPx(tablePadding.top, 5),
  };

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Box
        sx={(theme) => ({
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          display: "grid",
          fontWeight: 700,
          gridTemplateColumns: "1.4fr 0.8fr",
          textTransform: "uppercase",
        })}
      >
        <Box sx={baseCellSx}>Description</Box>
        <Box sx={baseCellSx}>Amount</Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1.4fr 0.8fr",
        }}
      >
        <Box sx={baseCellSx}>Less: Deductible Item</Box>
        <Box sx={{ ...baseCellSx, textAlign: "right" }}>(1,000)</Box>
      </Box>
      <Box
        sx={{
          bgcolor: (theme) => alpha(theme.palette.text.primary, 0.04),
          height: toPreviewPx(sectionGapHeight, 6, 4),
        }}
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1.4fr 0.8fr",
        }}
      >
        <Box sx={baseCellSx}>Chargeable Income Before Exempt Amount</Box>
        <Box sx={{ ...baseCellSx, textAlign: "right" }}>10,000</Box>
      </Box>
    </Box>
  );
};

const PdfSettingsDialog = ({
  densityOptions,
  onClose,
  onDensityChange,
  onSave,
  onSpacingOptionsChange,
  open,
  pdfSettingsContent,
  selectedDensity,
  spacingOptions,
  spacingPresets,
}) => {
  const resolvedSpacingPresets = {
    ...FALLBACK_SPACING_PRESETS,
    ...(spacingPresets || {}),
  };
  const customSpacingPresets = {
    compact: {
      ...CUSTOM_SPACING_PRESETS.compact,
      value: getPresetSpacingOptions(resolvedSpacingPresets, "compact"),
    },
    comfortable: {
      ...CUSTOM_SPACING_PRESETS.comfortable,
      value: getPresetSpacingOptions(resolvedSpacingPresets, "default"),
    },
    spacious: CUSTOM_SPACING_PRESETS.spacious,
  };
  const options = getDensityOptions(densityOptions?.options);
  const [draftDensity, setDraftDensity] = useState(selectedDensity);
  const [draftSpacingOptions, setDraftSpacingOptions] = useState(
    cloneSpacingOptions(sanitizeSpacingOptions(spacingOptions))
  );
  const [initialCustomizeSpacingOptions, setInitialCustomizeSpacingOptions] =
    useState(cloneSpacingOptions(sanitizeSpacingOptions(spacingOptions)));
  const [selectedCustomPreset, setSelectedCustomPreset] = useState(null);
  const [saving, setSaving] = useState(false);
  const [useCompactSettingsLayout, setUseCompactSettingsLayout] = useState(() =>
    shouldUseCompactPdfSettingsLayout()
  );
  const isCustomize = draftDensity === CUSTOM_DENSITY_VALUE;
  const displaySpacingOptions = getDisplaySpacingOptions({
    selectedDensity: draftDensity,
    spacingOptions: draftSpacingOptions,
    spacingPresets: resolvedSpacingPresets,
  });

  useEffect(() => {
    if (!open) return;

    setDraftDensity(selectedDensity);
    setDraftSpacingOptions(
      cloneSpacingOptions(sanitizeSpacingOptions(spacingOptions))
    );
    setInitialCustomizeSpacingOptions(
      cloneSpacingOptions(sanitizeSpacingOptions(spacingOptions))
    );
    setSelectedCustomPreset(null);
  }, [open, selectedDensity, spacingOptions]);

  useEffect(() => {
    if (!open) return undefined;

    const updateLayoutMode = () => {
      setUseCompactSettingsLayout(shouldUseCompactPdfSettingsLayout());
    };

    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);
    window.visualViewport?.addEventListener("resize", updateLayoutMode);

    return () => {
      window.removeEventListener("resize", updateLayoutMode);
      window.visualViewport?.removeEventListener("resize", updateLayoutMode);
    };
  }, [open]);

  const handleDensityChange = (nextValue) => {
    if (nextValue === CUSTOM_DENSITY_VALUE && !draftSpacingOptions) {
      const initialSpacing = cloneSpacingOptions(
        getPresetSpacingOptions(resolvedSpacingPresets, draftDensity)
      );
      setDraftSpacingOptions(initialSpacing);
      setInitialCustomizeSpacingOptions(cloneSpacingOptions(initialSpacing));
    }

    setDraftDensity(nextValue);
  };

  const handleCustomPresetChange = (nextValue) => {
    const preset = customSpacingPresets[nextValue];
    if (!preset) return;

    setSelectedCustomPreset(nextValue);
    setDraftSpacingOptions(cloneSpacingOptions(preset.value));
  };

  const handleResetCustomizeSpacing = () => {
    setSelectedCustomPreset(null);
    setDraftSpacingOptions(
      cloneSpacingOptions(
        initialCustomizeSpacingOptions ||
          getPresetSpacingOptions(resolvedSpacingPresets, "default")
      )
    );
  };

  const handleSpacingFieldChange = (path, rawValue) => {
    const parsedValue = parseFloat(rawValue);
    const nextValue =
      rawValue === "" || Number.isNaN(parsedValue) ? rawValue : parsedValue;

    setSelectedCustomPreset(null);
    setDraftSpacingOptions(
      setNestedValue(displaySpacingOptions, path, nextValue)
    );
  };

  const hasInvalidSpacing =
    isCustomize && hasSpacingErrors(displaySpacingOptions);

  const handleSave = async () => {
    if (hasInvalidSpacing || saving) return;

    const nextSpacingOptions = sanitizeSpacingOptions(
      draftSpacingOptions ||
        cloneSpacingOptions(
          getPresetSpacingOptions(resolvedSpacingPresets, "default")
        )
    );

    setSaving(true);
    try {
      await Promise.resolve(
        onSave?.({
          pdfDensity: draftDensity,
          pdfSpacingOptions:
            draftDensity === CUSTOM_DENSITY_VALUE
              ? nextSpacingOptions
              : null,
        })
      );

      if (draftDensity === CUSTOM_DENSITY_VALUE) {
        onSpacingOptionsChange?.(nextSpacingOptions);
      }

      onDensityChange?.(draftDensity);
      onClose?.();
    } catch {
      // Keep the dialog open. Callers can surface the actual error message.
    } finally {
      setSaving(false);
    }
  };

  const renderCustomContent = () => {
    if (typeof pdfSettingsContent !== "function") {
      return pdfSettingsContent || null;
    }

    return pdfSettingsContent({
      onPdfDensityChange: onDensityChange,
      onPdfSpacingOptionsChange: onSpacingOptionsChange,
      pdfDensityOptions: densityOptions,
      pdfSpacingOptions: draftSpacingOptions,
      selectedPdfDensityOption: draftDensity,
    });
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title='PDF Settings'
      subtitle='Adjust PDF density and spacing options.'
      icon={<SettingsIcon fontSize='small' />}
      maxWidth='lg'
      dialogSx={{
        "& .MuiDialogContent-root": {
          overflow: "hidden",
        },
      }}
      paperSx={{
        maxHeight: "calc(100vh - 64px)",
        overflow: "hidden",
      }}
      actions={
        <>
          <Button disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={hasInvalidSpacing || saving}
            variant='contained'
            onClick={handleSave}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </>
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: useCompactSettingsLayout ? 1.5 : 2.5,
          height: useCompactSettingsLayout
            ? "clamp(420px, calc(100dvh - 190px), 720px)"
            : "clamp(340px, calc(100vh - 240px), 660px)",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gap: useCompactSettingsLayout ? 1.5 : 2.5,
            gridTemplateColumns: useCompactSettingsLayout
              ? "1fr"
              : {
                  xs: "1fr",
                  md: "minmax(380px, 1.15fr) minmax(260px, 0.85fr)",
                },
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                mb: 1.5,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  alignItems: "flex-start",
                  display: "flex",
                  gap: 1.5,
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    Spacing Parameters
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: "block" }}
                  >
                    Choose a mode, or customize PDF table spacing. Spacing is{" "}
                    {SPACING_UNIT_LABEL}; font size is {FONT_UNIT_LABEL}.
                  </Typography>
                </Box>
                <Button
                  disabled={!isCustomize}
                  onClick={handleResetCustomizeSpacing}
                  size='small'
                  startIcon={<RestartAltIcon fontSize='small' />}
                  sx={{ flexShrink: 0 }}
                >
                  Reset
                </Button>
              </Box>

              {options.length > 1 && (
                <OptionSwitch
                  disabled={densityOptions?.disabled}
                  options={options}
                  value={draftDensity}
                  onChange={handleDensityChange}
                  sx={{
                    width: "100%",
                    "& .MuiToggleButton-root": {
                      flex: 1,
                      minHeight: useCompactSettingsLayout ? 32 : 36,
                      py: useCompactSettingsLayout ? 0.35 : 0.5,
                    },
                    ...densityOptions?.sx,
                  }}
                />
              )}

              {isCustomize && (
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    gap: 1,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ flexShrink: 0 }}
                  >
                    Quick preset
                  </Typography>
                  <OptionSwitch
                    options={CUSTOM_SPACING_PRESET_OPTIONS}
                    size='small'
                    value={selectedCustomPreset || ""}
                    onChange={handleCustomPresetChange}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      "& .MuiToggleButton-root": {
                        fontSize: "0.68rem",
                        lineHeight: 1.1,
                        minWidth: 0,
                        px: 0.85,
                        py: 0.3,
                      },
                    }}
                  />
                </Box>
              )}
            </Box>

            <Box
              sx={{
                minHeight: 0,
                overflowY: "auto",
                pb: 0.5,
                pr: 1,
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: (t) => alpha(t.palette.primary.main, 0.2),
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: (t) => alpha(t.palette.primary.main, 0.3),
                  },
                },
              }}
            >
              {SPACING_GROUPS.map((group, index) => (
                <Accordion
                  key={group.id}
                  defaultExpanded={index === 0}
                  disableGutters
                  elevation={0}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    "&:before": { display: "none" },
                    "& + &": { mt: 1 },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        {group.title}
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {group.description}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Box
                      sx={{
                        display: "grid",
                        gap: 1.5,
                        gridTemplateColumns: useCompactSettingsLayout
                          ? "1fr"
                          : {
                              xs: "1fr",
                              sm: "repeat(2, minmax(0, 1fr))",
                            },
                      }}
                    >
                      {group.fields.map((field) => {
                        const value = getNestedValue(
                          displaySpacingOptions,
                          field.path
                        );
                        const errorText = isCustomize
                          ? validateSpacingField(field, value)
                          : "";
                        const unitLabel = getFieldUnitLabel(field);

                        return (
                          <TextField
                            key={field.path.join(".")}
                            label={field.label}
                            type='number'
                            size='small'
                            value={value ?? ""}
                            placeholder={value == null ? "Auto" : undefined}
                            disabled={!isCustomize}
                            error={Boolean(errorText)}
                            helperText={errorText || unitLabel}
                            onChange={(event) =>
                              handleSpacingFieldChange(
                                field.path,
                                event.target.value
                              )
                            }
                            inputProps={{
                              max: getFieldMaxValue(field),
                              min: getFieldMinValue(field),
                              step: unitLabel === FONT_UNIT_LABEL ? 0.5 : 0.1,
                            }}
                          />
                        );
                      })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              display: useCompactSettingsLayout
                ? "none"
                : { xs: "none", md: "block" },
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                gap: 1.5,
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant='body2' sx={{ fontWeight: 600 }}>
                Preview
              </Typography>
            </Box>
            <SpacingPreview spacingOptions={displaySpacingOptions} />
          </Box>
        </Box>

        {pdfSettingsContent && (
          <>
            <Divider />
            {renderCustomContent()}
          </>
        )}
      </Box>
    </BaseDialog>
  );
};

export default PdfSettingsDialog;
